import { z } from 'zod/v3'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import {
  DetailAsideSection,
  DetailPanelAside,
  DetailPanelLayout,
} from '@/components/compliance/detail-primitives'
import { PageHeaderBreadcrumb } from '@/components/layout/PageHeaderActions'
import { SystemDescriptionContent } from '@/components/screenata/SystemDescriptionForm'
import { DocumentRelatedTest } from '@/components/screenata/vault-document/DocumentRelatedTest'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/system-description'
)({
  component: SystemDescriptionPage,
  validateSearch: z.object({
    from: z.literal('documents').optional(),
    testId: z.string().trim().min(1).optional(),
  }),
  staticData: {
    pageHeader: { title: 'System Description', pad: false, scroll: false },
  },
})

function SystemDescriptionPage() {
  const { from, testId } = Route.useSearch()
  const { workspaceId } = Route.useParams()
  const keyDocuments = useQuery(trpc.vaultDocument.keyDocuments.queryOptions())
  const entry = keyDocuments.data?.find(
    (document) => document.deliverableKey === 'system_description'
  )

  return (
    <>
      <PageHeaderBreadcrumb>
        {testId ? (
          <Link
            to='/workspaces/$workspaceId/controls'
            params={{ workspaceId }}
            search={{ test: testId }}
            className='font-heading text-text-subtle hover:text-foreground text-[15px] font-medium'
          >
            Back to Test
          </Link>
        ) : (
          <Link
            to='/workspaces/$workspaceId/documents'
            params={{ workspaceId }}
            search={from === 'documents' ? { tab: 'key' } : {}}
            className='font-heading text-text-subtle hover:text-foreground text-[15px] font-medium'
          >
            Documents
          </Link>
        )}
      </PageHeaderBreadcrumb>
      <DetailPanelLayout>
        <div className='min-w-0 flex-1'>
          <SystemDescriptionContent />
        </div>
        <DetailPanelAside className='bg-card'>
          <DetailAsideSection title='Related Tests'>
            <div className='w-full space-y-2'>
              {keyDocuments.isPending ? (
                <p className='text-muted-foreground text-xs'>
                  Loading related Tests…
                </p>
              ) : keyDocuments.isError ? (
                <p role='alert' className='text-destructive text-xs'>
                  Could not load related Tests.
                </p>
              ) : entry?.tests.length ? (
                entry.tests.map((test) => (
                  <DocumentRelatedTest
                    key={test.statusId}
                    test={test}
                    workspaceId={workspaceId}
                    showStatus={false}
                  />
                ))
              ) : (
                <p className='text-muted-foreground text-xs'>
                  No related Tests.
                </p>
              )}
            </div>
          </DetailAsideSection>
        </DetailPanelAside>
      </DetailPanelLayout>
    </>
  )
}
